import * as React from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { authService, type CallsignLoginResponse } from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";
import { hasNasionalAssignmentMissingPrivileges } from "@/utils/privileges";
import { toast } from "sonner";
import { AUTH_RED } from "./authTheme";

type AuthMethod = "password" | "iar" | null;

export function LoginPage() {
  const { refreshUser } = useAuth();
  const [, setLocation] = useLocation();
  const [step, setStep] = React.useState(1);
  const [callsign, setCallsign] = React.useState("");
  const [authMethod, setAuthMethod] = React.useState<AuthMethod>(null);
  const [password, setPassword] = React.useState("");
  const [iar, setIar] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [forceIarRecovery, setForceIarRecovery] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "forgot-no-email") return;
    setForceIarRecovery(true);
    const cs = params.get("callsign");
    if (cs?.trim()) {
      setCallsign(cs.trim().toUpperCase());
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await authService.checkCallsign(callsign);
      if (forceIarRecovery) {
        setAuthMethod("iar");
      } else {
        setAuthMethod(result.authMethod);
      }
      setCallsign(result.callsign);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to check callsign.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      let loginResult: CallsignLoginResponse | null = null;
      if (authMethod === "password") {
        loginResult = await authService.loginWithPassword(callsign, password);
      } else if (authMethod === "iar") {
        loginResult = await authService.loginWithIAR(callsign, iar);
      }

      if (forceIarRecovery && authMethod === "iar") {
        try {
          sessionStorage.setItem("orari_prompt_password_after_forgot_iar", "1");
        } catch {
          /* ignore */
        }
      }

      const userAfterMe = await refreshUser();

      if (hasNasionalAssignmentMissingPrivileges(userAfterMe)) {
        toast.warning("Penugasan nasional tanpa hak akses", {
          description:
            "Penugasan tingkat nasional Anda belum memiliki daftar hak akses. Fitur admin nasional tidak diaktifkan sampai pengurus menambahkan hak akses pada penugasan tersebut.",
        });
      }

      if (loginResult?.user?.legacyUser === true) {
        toast.warning("Login sementara (SDPPI tidak tersedia)", {
          description:
            "Beberapa fitur admin dinonaktifkan sampai sinkronisasi SDPPI berhasil. Silakan login ulang saat SDPPI sudah normal.",
        });
      }

      setLocation("/user");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setPassword("");
    setIar("");
    setError("");
    setAuthMethod(null);
  };

  return (
    <Card className="w-full shadow-lg border-border/50">
      <CardHeader className="space-y-3">
        <CardTitle className={`text-3xl font-bold ${AUTH_RED.title}`}>Masuk</CardTitle>
        <CardDescription className="text-muted-foreground">
          {step === 1
            ? "Masukkan callsign Anda untuk melanjutkan"
            : authMethod === "password"
              ? "Masukkan kata sandi Anda"
              : "Masukkan IAR Anda"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {forceIarRecovery && step === 1 && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-950">
            Akun ini tidak memiliki reset via email. Gunakan <strong>IAR</strong> di langkah berikutnya. Setelah masuk,
            selesaikan pengaturan profil termasuk kata sandi jika diminta.
          </div>
        )}
        {forceIarRecovery && step === 2 && authMethod === "iar" && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-950">
            Masukkan nomor IAR yang sesuai dengan akun Anda. Ini memverifikasi identitas Anda tanpa email.
          </div>
        )}

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex items-center gap-2 ${step >= 1 ? AUTH_RED.stepText : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? AUTH_RED.stepActive : 'bg-gray-200'}`}>
              1
            </div>
            <span className="text-sm font-medium">Identitas</span>
          </div>
          <div className="flex-1 h-px bg-gray-200"></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? AUTH_RED.stepText : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? AUTH_RED.stepActive : 'bg-gray-200'}`}>
              2
            </div>
            <span className="text-sm font-medium">Verifikasi</span>
          </div>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1Submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="callsign" className="text-foreground">Callsign</Label>
              <Input
                id="callsign"
                type="text"
                placeholder="Callsign Anda"
                value={callsign}
                onChange={(e) => setCallsign(e.target.value.toUpperCase())}
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
                {error}
              </div>
            )}

            <Button type="submit" className={`w-full ${AUTH_RED.button}`} disabled={isLoading}>
              {isLoading ? "Memeriksa..." : "Lanjut"}
            </Button>

            <button
              type="button"
              onClick={() => setLocation("/auth/forgot-password")}
              className={`w-full text-sm ${AUTH_RED.link} underline text-center`}
            >
              Lupa kata sandi?
            </button>
          </form>
        ) : (
          <form onSubmit={handleStep2Submit} className="space-y-5">
            <button
              type="button"
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground text-sm flex items-center gap-2 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali ke callsign
            </button>

            <div className="space-y-2">
              <Label className="text-foreground">Callsign</Label>
              <div className="px-3 py-2 bg-muted border border-border rounded-md text-foreground">
                {callsign}
              </div>
            </div>

            {authMethod === "password" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-foreground">Kata Sandi</Label>
                  <button
                    type="button"
                    onClick={() => setLocation("/auth/forgot-password")}
                    className={`text-sm ${AUTH_RED.link} underline`}
                  >
                    Lupa kata sandi?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="iar" className="text-foreground">IAR</Label>
                <Input
                  id="iar"
                  type="text"
                  placeholder="Masukkan IAR Anda"
                  value={iar}
                  onChange={(e) => setIar(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
                {error}
              </div>
            )}

            <Button type="submit" className={`w-full ${AUTH_RED.button}`} disabled={isLoading}>
              {isLoading ? "Masuk..." : "Masuk"}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
